import { useCallback, useState } from 'react';
import { AppShell } from '@/components/shell/AppShell';
import type { AppView } from '@/components/shell/Header';
import { TexasMap } from '@/components/map/TexasMap';
import { InsightDrawer } from '@/components/panel/InsightDrawer';
import { ReportBuilder } from '@/components/reports/ReportBuilder';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { WarningsBanner } from '@/components/common/WarningsBanner';
import { useMarketData } from '@/hooks/useMarketData';
import { useRefreshScheduler } from '@/hooks/useRefreshScheduler';
import { useDataStore } from '@/store/dataStore';

export default function App() {
  const { status, refresh } = useMarketData();
  const warnings = useDataStore((s) => s.warnings);
  const error = useDataStore((s) => s.error);
  const geographies = useDataStore((s) => s.geographies);
  const showInitialLoading = status === 'loading' && geographies.length === 0;
  const [view, setView] = useState<AppView>('map');

  const handleTick = useCallback(() => {
    refresh();
  }, [refresh]);

  useRefreshScheduler(handleTick);

  return (
    <AppShell onRefresh={refresh} isLoading={status === 'loading'} view={view} onViewChange={setView}>
      <WarningsBanner warnings={warnings} />
      {showInitialLoading && <LoadingState />}
      {status === 'error' && geographies.length === 0 && <ErrorState message={error ?? 'Unknown error'} onRetry={refresh} />}
      {!showInitialLoading && geographies.length > 0 && view === 'map' && (
        <div className="app-map-area">
          <TexasMap />
          <InsightDrawer />
        </div>
      )}
      {!showInitialLoading && geographies.length > 0 && view === 'reports' && <ReportBuilder />}
    </AppShell>
  );
}
